import { useState } from 'react'
import { Card, Button, Badge, Modal } from '@/components/common'

export interface Member {
  id: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'member'
  joinedAt: string
  isActive: boolean
}

interface MemberListProps {
  members: Member[]
  currentUserId?: string
  onRemove?: (memberId: string) => void
  onUpdateRole?: (memberId: string, newRole: 'owner' | 'admin' | 'member') => void
  onInvite?: () => void
}

export const MemberList: React.FC<MemberListProps> = ({
  members,
  currentUserId,
  onRemove,
  onUpdateRole,
  onInvite,
}) => {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)

  const handleRemoveClick = (member: Member) => {
    setMemberToRemove(member)
    setShowRemoveConfirm(member.id)
  }

  const handleConfirmRemove = () => {
    if (memberToRemove && onRemove) {
      onRemove(memberToRemove.id)
      setShowRemoveConfirm(null)
      setMemberToRemove(null)
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      case 'admin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      case 'member':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const canRemove = (member: Member) => {
    // Can't remove yourself
    if (member.id === currentUserId) return false
    // Can't remove owners
    if (member.role === 'owner') return false
    return true
  }

  const canUpdateRole = (member: Member) => {
    // Can't update your own role
    if (member.id === currentUserId) return false
    // Can't update owner role
    if (member.role === 'owner') return false
    return true
  }

  return (
    <>
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-main-light dark:text-text-main-dark">
              Team Members
            </h3>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mt-1">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          {onInvite && (
            <Button variant="primary" size="sm" onClick={onInvite}>
              <span className="material-symbols-outlined mr-1" style={{ fontSize: '18px' }}>
                person_add
              </span>
              Invite Member
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {members.length === 0 ? (
            <div className="text-center py-8">
              <span
                className="material-symbols-outlined text-4xl text-text-sub-light dark:text-text-sub-dark mb-2"
                style={{ fontSize: '48px' }}
              >
                groups
              </span>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                No members yet. Invite your first team member!
              </p>
            </div>
          ) : (
            members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-text-main-light dark:text-text-main-dark">
                        {member.name}
                      </span>
                      {member.id === currentUserId && (
                        <Badge variant="default" size="sm">
                          You
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-text-sub-light dark:text-text-sub-dark">
                      {member.email}
                    </div>
                    <div className="text-xs text-text-sub-light dark:text-text-sub-dark mt-1">
                      Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={getRoleColor(member.role)} size="sm">
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </Badge>
                  {canUpdateRole(member) && onUpdateRole && (
                    <select
                      value={member.role}
                      onChange={(e) =>
                        onUpdateRole(member.id, e.target.value as 'owner' | 'admin' | 'member')
                      }
                      className="text-xs px-2 py-1 rounded border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark text-text-main-light dark:text-text-main-dark"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                  {canRemove(member) && onRemove && (
                    <button
                      onClick={() => handleRemoveClick(member)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      title="Remove member"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                        delete
                      </span>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={showRemoveConfirm !== null}
        onClose={() => {
          setShowRemoveConfirm(null)
          setMemberToRemove(null)
        }}
        title="Remove Team Member"
      >
        {memberToRemove && (
          <div className="space-y-4">
            <p className="text-text-main-light dark:text-text-main-dark">
              Are you sure you want to remove <strong>{memberToRemove.name}</strong> from your
              organization?
            </p>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
              They will lose access to all organization resources and data. This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRemoveConfirm(null)
                  setMemberToRemove(null)
                }}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmRemove}>
                Remove Member
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

